import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  FaYoutube,
  FaSpotify,
  FaApple,
  FaAmazon,
  FaWhatsapp,
} from "react-icons/fa6";
import { API_BASE } from "../lib/config";
import { externalLinkProps, isPresent } from "../lib/links";
import FloatingYouTubePlayer from "../components/FloatingYouTubePlayer";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";

// Detail-page Watch & Listen shows only these four platforms — center-aligned.
const STREAMING_DEFS = [
  { key: "youtube", label: "YouTube", Icon: FaYoutube },
  { key: "spotify", label: "Spotify", Icon: FaSpotify },
  { key: "apple_music", label: "Apple Music", Icon: FaApple },
  { key: "amazon_music", label: "Amazon Music", Icon: FaAmazon },
];

const ALL_LANGS = [
  { code: "en", label: "English" },
  { code: "te", label: "తెలుగు" },
  { code: "hi", label: "हिन्दी" },
];

const SectionTitle = ({ overline, children, align = "left" }) => (
  <div className={align === "center" ? "text-center" : ""}>
    <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
      {overline}
    </p>
    {children && (
      <h2 className="mt-4 font-heading font-normal text-charcoal text-3xl md:text-4xl tracking-tight">
        {children}
      </h2>
    )}
    <div
      className={`mt-6 w-10 h-px bg-muted_gold ${align === "center" ? "mx-auto" : ""}`}
    />
  </div>
);

const nonEmpty = (arr) =>
  Array.isArray(arr) && arr.some((v) => typeof v === "string" && v.trim());

const getYouTubeEmbedUrl = (url) => {
  if (!url) return "";

  try {
    const u = new URL(url);

    if (u.hostname.includes("youtu.be")) {
      const videoId = u.pathname.replace("/", "");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
    }

    if (u.hostname.includes("youtube.com")) {
      const videoId = u.searchParams.get("v");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
    }
  } catch {
    return "";
  }

  return "";
};

const upsertMetaTag = (attribute, key, content) => {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const upsertCanonicalLink = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
};

const StotramDetailPage = () => {
  const { slug } = useParams();
  const [stotram, setStotram] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeLang, setActiveLang] = useState("en");

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
      setStotram(null);

    axios
      .get(`${API_BASE}/stotrams/${slug}`)
      .then(async (res) => {
        setStotram(res.data);
        const relatedSlugs = res.data.related_slugs || [];
        if (relatedSlugs.length > 0) {
          const results = await Promise.allSettled(
            relatedSlugs.map((rs) => axios.get(`${API_BASE}/stotrams/${rs}`)),
          );
          setRelated(
  results
    .filter((r) => r.status === "fulfilled")
    .map((r) => r.value.data),
);
} else {
  setRelated([]);
}

setLoading(false);
})
.catch((err) => {
  if (err?.response?.status === 404) setNotFound(true);
  setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    if (!stotram) return;

    const seoTitle =
      stotram.seo_title ||
      `${stotram.title} — Sanskrit Text, Transliteration & Meaning | TATTVAM`;

    const seoDescription =
      stotram.seo_description ||
      stotram.description ||
      `Explore ${stotram.title} with Sanskrit text, transliteration, meaning, devotional video and audio links.`;

    const canonicalUrl = `https://tattvam.media/stotrams/${stotram.slug}`;

    const socialImageUrl = stotram.image_url?.startsWith("http")
  ? stotram.image_url
  : `https://tattvam.media${
      stotram.image_url?.startsWith("/")
        ? stotram.image_url
        : `/${stotram.image_url}`
    }`;

    document.title = seoTitle;

    upsertMetaTag("name", "description", seoDescription);

    upsertMetaTag("property", "og:type", "website");
    upsertMetaTag("property", "og:site_name", "TATTVAM");
    upsertMetaTag("property", "og:title", seoTitle);
    upsertMetaTag("property", "og:description", seoDescription);

    const imageType = stotram.image_url?.toLowerCase().endsWith(".png")
      ? "image/png"
      : "image/jpeg";

    upsertMetaTag("property", "og:url", canonicalUrl);
    upsertMetaTag("property", "og:image", socialImageUrl);
    upsertMetaTag("property", "og:image:secure_url", socialImageUrl);
    upsertMetaTag("property", "og:image:type", imageType);
    upsertMetaTag(
      "property",
      "og:image:alt",
      `${stotram.title} — devotional artwork`,
    );

    upsertMetaTag("name", "twitter:card", "summary_large_image");
    upsertMetaTag("name", "twitter:title", seoTitle);
    upsertMetaTag("name", "twitter:description", seoDescription);
    upsertMetaTag("name", "twitter:image", socialImageUrl);
    upsertMetaTag(
      "name",
      "twitter:image:alt",
      `${stotram.title} — devotional artwork`,
    );

    upsertCanonicalLink(canonicalUrl);

    return () => {
      document.title = "TATTVAM · Seek the Stillness";

      upsertMetaTag(
        "name",
        "description",
        "Explore scholar-verified Sanskrit stotrams with complete texts, transliterations, meanings, devotional videos and audio links.",
      );

      upsertMetaTag("property", "og:title", "TATTVAM · Seek the Stillness");
      upsertMetaTag(
        "property",
        "og:description",
        "Scholar-verified Sanskrit stotrams with complete texts, transliterations, meanings, devotional videos and audio links.",
      );
            upsertMetaTag(
        "property",
        "og:image",
        "https://tattvam.media/images/tattvam-social-preview.png",
      );
      upsertMetaTag(
        "property",
        "og:image:secure_url",
        "https://tattvam.media/images/tattvam-social-preview.png",
      );
      upsertMetaTag("property", "og:image:type", "image/png");
      upsertMetaTag(
        "property",
        "og:image:alt",
        "TATTVAM — Scholar-Verified Sanskrit Stotrams",
      );
      upsertMetaTag("property", "og:url", "https://tattvam.media/");

      const canonicalLink = document.head.querySelector(
        'link[rel="canonical"]',
      );
      canonicalLink?.remove();
    };
  }, [stotram]);

  // Language tabs are dynamic — a tab appears only when that language has
  // transliteration text. Meaning is optional and never gates the tab.
  const availableLangs = useMemo(() => {
    if (!stotram) return [];
    const tr = stotram.transliteration || {};
    return ALL_LANGS.filter((l) => nonEmpty(tr[l.code]));
  }, [stotram]);

  // Auto-select the first available language if the current pick is empty.
  useEffect(() => {
    if (!availableLangs.length) return;
    if (!availableLangs.find((l) => l.code === activeLang)) {
      setActiveLang(availableLangs[0].code);
    }
  }, [availableLangs, activeLang]);

  // Build verse pairs for the active language by pairing array indices.
  const { verses, hasMeaningColumn } = useMemo(() => {
    if (!stotram) return { verses: [], hasMeaningColumn: false };
    const trArr = (stotram.transliteration || {})[activeLang] || [];
    const mnArr = (stotram.meaning || {})[activeLang] || [];
    const n = Math.max(trArr.length, mnArr.length);
    const rows = [];
    for (let i = 0; i < n; i++) {
      const t = (trArr[i] || "").trim();
      const m = (mnArr[i] || "").trim();
      if (t || m) rows.push({ transliteration: t, meaning: m });
    }
    const meaningCol = rows.some((r) => r.meaning.length > 0);
    return { verses: rows, hasMeaningColumn: meaningCol };
  }, [stotram, activeLang]);

  const streaming = useMemo(() => {
    if (!stotram?.streaming) return [];
    return STREAMING_DEFS.filter((d) =>
      isPresent(stotram.streaming[d.key]),
    ).map((d) => ({ ...d, href: stotram.streaming[d.key] }));
  }, [stotram]);

  const youtubeEmbedUrl = useMemo(
  () => getYouTubeEmbedUrl(stotram?.streaming?.youtube),
  [stotram],
);

  const filteredCredits = useMemo(() => {
    if (!stotram?.credits) return [];
   return [
    ["Knowledge & Creative Partner", stotram.credits.research],
    ["Music Composition & Production", stotram.credits.music],
    ["Video Production Partner", stotram.credits.visuals],
    ["Vocals", stotram.credits.vocals],
  ].filter(([, v]) => isPresent(v));
}, [stotram]);

  if (loading) {
    return (
      <main
        data-testid="stotram-detail-loading"
        className="bg-ivory pt-40 min-h-screen"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="h-64 bg-ivory-deeper animate-pulse" />
          <div className="mt-8 h-8 w-2/3 bg-ivory-deeper animate-pulse" />
          <div className="mt-4 h-4 w-1/2 bg-ivory-deeper animate-pulse" />
        </div>
      </main>
    );
  }

  if (notFound || !stotram) {
    return (
      <main
        data-testid="stotram-not-found"
        className="bg-ivory pt-40 pb-40 min-h-screen text-center"
      >
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
            Not Found
          </p>
          <h1 className="mt-6 font-heading text-5xl text-charcoal font-normal">
            This stotram is not in the library yet.
          </h1>
          <div className="mt-10">
            <Link
              to="/stotrams"
              className="inline-block border border-charcoal/40 text-charcoal px-8 py-4 text-xs tracking-[0.32em] uppercase font-medium hover:border-muted_gold hover:text-muted_gold transition-colors duration-500"
            >
              Return to the Library
            </Link>
          </div>
        </div>
      </main>
    );
  }
const shareUrl = `https://tattvam.media/stotrams/${stotram.slug}`;

const whatsappMessage = `Listen to ${stotram.title} with complete text, transliteration and meaning on TATTVAM:\n\n${shareUrl}`;

const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(
  whatsappMessage,
)}`;

  const showMultilingual = availableLangs.length > 0 && verses.length > 0;
  const showAttrDedicCtxBenefits =
    isPresent(stotram.attributed_to) ||
    isPresent(stotram.dedicated_to) ||
    isPresent(stotram.context) ||
    stotram.benefits?.length > 0;

  return (
    <main data-testid="stotram-detail-page" className="bg-ivory">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7">
            <Breadcrumb className="mb-8">
              <BreadcrumbList className="text-charcoal-soft">
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>

                <BreadcrumbSeparator />

                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/stotrams">Stotrams</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>

                <BreadcrumbSeparator />

                <BreadcrumbItem className="min-w-0">
                  <BreadcrumbPage className="whitespace-normal break-words text-charcoal">
                    {stotram.title}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <p
              data-testid="stotram-deity"
              className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold"
            >
              {stotram.deity}
            </p>
            <h1
              data-testid="stotram-title"
              className="mt-6 font-heading font-normal text-charcoal text-5xl sm:text-6xl md:text-7xl leading-[1.05] tracking-tight"
            >
              {stotram.title}
            </h1>
            <p
              data-testid="stotram-sanskrit-title"
              lang="hi"
              className="mt-2 indic-text italic text-2xl md:text-3xl text-charcoal-soft"
            >
              {stotram.sanskrit_title}
            </p>
            <div className="mt-6 w-12 h-px bg-muted_gold" />
            <p className="mt-6 max-w-xl text-charcoal-soft text-base md:text-lg leading-relaxed">
              {stotram.description}
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-square overflow-hidden border border-charcoal/15 bg-ivory-deeper">
              <img
                src={stotram.image_url?.startsWith("/") ? stotram.image_url : `/${stotram.image_url}`}
                alt={`${stotram.title} — devotional artwork`}
                data-testid="stotram-image"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Attributed To / Dedicated To / Context / Benefits — clean 2×2 on desktop */}
      {showAttrDedicCtxBenefits && (() => {
        const hasA = isPresent(stotram.attributed_to);
        const hasD = isPresent(stotram.dedicated_to);
        const hasC = isPresent(stotram.context);
        const hasB = stotram.benefits?.length > 0;
        const cell = "flex flex-col";
        return (
          <section className="py-10 md:py-14 max-w-5xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 md:gap-y-12">
              {hasD && (
  <div data-testid="section-dedicated-to" className={cell}>
    <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
      Dedicated To
    </p>
    <p className="mt-4 text-charcoal text-base md:text-lg leading-relaxed">
      {stotram.dedicated_to}
    </p>
  </div>
)}
{hasA && (
  <div data-testid="section-attributed-to" className={cell}>
    <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
      Attributed To
    </p>
    <p className="mt-4 text-charcoal text-base md:text-lg leading-relaxed">
      {stotram.attributed_to}
    </p>
  </div>
              )}
              {hasC && (
                <div
                  data-testid="section-context"
                  className={`${cell} ${!hasB ? "md:col-span-2" : ""}`}
                >
                  <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
                    Context
                  </p>
                  <p className="mt-4 text-charcoal text-base md:text-lg leading-relaxed whitespace-pre-line">
                    {stotram.context}
                  </p>
                </div>
              )}
              {hasB && (
                <div data-testid="section-benefits" className={cell}>
                  <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
                    Benefits of Chanting
                  </p>
                  <div className="mt-4 space-y-4 text-charcoal text-base md:text-lg leading-relaxed">
                    {stotram.benefits.map((b, i) => (
                      <p key={i}>{b}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* When to Chant — standalone row, only when present */}
      {isPresent(stotram.when_to_chant) && (
        <section
          data-testid="section-when-to-chant"
          className="pt-2 pb-10 md:pb-14 max-w-5xl mx-auto px-6 md:px-12"
        >
          <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
            When to Chant
          </p>
          <p className="mt-4 text-charcoal text-base md:text-lg leading-relaxed">
            {stotram.when_to_chant}
          </p>
        </section>
      )}

      {/* Watch & Listen — center-aligned, 4 platforms only */}
      {streaming.length > 0 && (
        <section
          data-testid="section-watch-listen"
          className="py-16 bg-ivory-deeper"
        >
          <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
            <SectionTitle overline="Watch & Listen" align="center">
              On all platforms
            </SectionTitle>

  {youtubeEmbedUrl && (
    <FloatingYouTubePlayer
      embedUrl={youtubeEmbedUrl}
      title={`${stotram.title} video`}
    />
  )}

  <div className="mt-10 mx-auto flex flex-wrap justify-center gap-4 md:gap-6">
              {streaming.map(({ key, label, Icon, href }) => (
                <a
                  key={key}
                  href={href}
                  {...externalLinkProps(href)}
                  data-testid={`stotram-platform-${key}`}
                  aria-label={`Listen on ${label}`}
                  className="group flex flex-col items-center justify-center gap-3 border border-charcoal/25 bg-ivory w-36 md:w-40 py-7 hover:border-muted_gold transition-all duration-500"
                >
                  <Icon
                    size={24}
                    className="text-charcoal group-hover:text-muted_gold transition-colors duration-500"
                  />
                  <span className="text-xs tracking-[0.24em] uppercase font-medium text-charcoal">
                    {label}
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-8">
              <a
                href={whatsappShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-share-button"
                aria-label={`Share ${stotram.title} on WhatsApp`}
                className="inline-flex items-center justify-center gap-2 border border-charcoal/30 bg-ivory px-6 py-3 text-xs tracking-[0.18em] uppercase font-medium text-charcoal hover:border-muted_gold hover:text-muted_gold transition-colors duration-500"
              >
                <FaWhatsapp size={18} aria-hidden="true" />
                <span>Share on WhatsApp</span>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Transliteration & Meaning — verse-aware, responsive */}
      {showMultilingual && (
        <section
          data-testid="section-multilingual"
          className="py-20 max-w-5xl mx-auto px-6 md:px-12"
        >
          <div className="text-center">
            <SectionTitle overline="Transliteration & Meaning" align="center">
              In your language
            </SectionTitle>
          </div>

          {/* Dynamic language tabs */}
          <div
            role="tablist"
            aria-label="Choose language"
            data-testid="language-tabs"
            className="mt-10 flex items-center justify-center gap-2 border-b border-charcoal/15"
          >
            {availableLangs.map((l) => (
              <button
                key={l.code}
                role="tab"
                aria-selected={activeLang === l.code}
                data-testid={`lang-tab-${l.code}`}
                onClick={() => setActiveLang(l.code)}
                lang={l.code}
                className={`px-5 py-3 text-base md:text-lg font-body ${
                  l.code !== "en"
                    ? "indic-text"
                    : "tracking-[0.24em] uppercase text-sm font-medium"
                } transition-colors duration-500 border-b-2 -mb-px ${
                  activeLang === l.code
                    ? "border-muted_gold text-charcoal"
                    : "border-transparent text-charcoal-soft hover:text-charcoal"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Column headings — shown once at the top of the section */}
          <div
            className={`mt-12 hidden md:grid ${hasMeaningColumn ? "md:grid-cols-2" : "md:grid-cols-1"} gap-12 md:gap-16`}
          >
            <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
              Transliteration
            </p>
            {hasMeaningColumn && (
              <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
                Meaning
              </p>
            )}
          </div>

          {/* Verses */}
          <div className="mt-6 md:mt-8 flex flex-col gap-10 md:gap-14">
            {verses.map((v, i) => (
              <div
                key={i}
                data-testid={`verse-${i + 1}`}
                className={`grid grid-cols-1 gap-4 md:gap-16 ${
                  hasMeaningColumn ? "md:grid-cols-2" : "md:grid-cols-1"
                }`}
              >
                <pre
                  data-testid={`verse-${i + 1}-transliteration-${activeLang}`}
                  lang={activeLang}
                  className={`m-0 text-charcoal whitespace-pre-wrap break-words text-lg md:text-xl leading-[1.85] font-medium ${
                    activeLang === "en" ? "font-heading" : "indic-text"
                  }`}
                >
{v.transliteration}
                </pre>
                {hasMeaningColumn && v.meaning && (
                  <p
                    data-testid={`verse-${i + 1}-meaning-${activeLang}`}
                    lang={activeLang}
                    className={`text-charcoal text-base md:text-lg leading-[1.85] font-medium whitespace-pre-line ${
                      activeLang !== "en" ? "indic-text" : ""
                    }`}
                  >
                    {v.meaning}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Credits */}
      {filteredCredits.length > 0 && (
        <section
          data-testid="section-credits"
          className="py-16 max-w-4xl mx-auto px-6 md:px-12"
        >
          <SectionTitle overline="Credits">With gratitude</SectionTitle>
          <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6">
            {filteredCredits.map(([k, v]) => (
              <div key={k} className="border-t border-charcoal/15 pt-5">
                <dt className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
                  {k}
                </dt>
                <dd className="mt-2 text-charcoal">{v}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* Related Chantings */}
      {related.length > 0 && (
        <section
          data-testid="section-related"
          className="py-20 bg-ivory-deeper"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14">
              <SectionTitle overline="Related Chantings" align="center">
                Continue listening
              </SectionTitle>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/stotrams/${r.slug}`}
                  data-testid={`related-card-${r.slug}`}
                  className="museum-plaque border border-charcoal/15 bg-ivory flex flex-col"
                >
                  <div className="aspect-square overflow-hidden bg-ivory-deeper">
                    <img
                      src={r.image_url?.startsWith("/") ? r.image_url : `/${r.image_url}`}
                      alt={`${r.title} — devotional artwork`}
                      className="plaque-image w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-8">
                    <p className="text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold">
                      {r.deity}
                    </p>
                    <h3 className="mt-3 font-heading font-normal text-2xl text-charcoal">
                      {r.title}
                    </h3>
                    <p className="mt-1 indic-text italic text-charcoal-soft">
                      {r.sanskrit_title}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default StotramDetailPage;
